import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { X, ScanLine } from "lucide-react";

/**
 * Barcode scanner using the browser's native BarcodeDetector API
 * (Chromium desktop, Chrome Android, Safari 17+). Falls back to manual text entry.
 * Props: open (bool), onOpenChange(bool), onDetected(code:string)
 */
export default function BarcodeScanner({ open, onOpenChange, onDetected }) {
  const { t } = useApp();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const runningRef = useRef(false);
  const [supported, setSupported] = useState(true);
  const [error, setError] = useState("");
  const [manual, setManual] = useState("");

  useEffect(() => {
    if (!open) {
      stop();
      setManual("");
      setError("");
      return;
    }
    const hasApi = "BarcodeDetector" in window;
    setSupported(hasApi);
    if (!hasApi) return;
    start();
    return stop; // cleanup on close
    // eslint-disable-next-line
  }, [open]);

  const stop = () => {
    runningRef.current = false;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      const detector = new window.BarcodeDetector({
        formats: ["ean_13", "ean_8", "code_128", "code_39", "upc_a", "upc_e", "qr_code", "itf"],
      });
      runningRef.current = true;
      const loop = async () => {
        if (!runningRef.current) return;
        try {
          const codes = await detector.detect(videoRef.current);
          if (codes && codes[0] && codes[0].rawValue) {
            runningRef.current = false;
            onDetected(codes[0].rawValue);
            onOpenChange(false);
            return;
          }
        } catch (e) { /* frame not ready */ }
        setTimeout(loop, 250);
      };
      loop();
    } catch (e) {
      setError(String(e?.message || e));
    }
  };

  const submitManual = (e) => {
    e.preventDefault();
    if (manual.trim()) { onDetected(manual.trim()); onOpenChange(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ScanLine className="w-5 h-5 text-[#B45309]" /> {t("scan_barcode")}</DialogTitle>
        </DialogHeader>

        {supported && !error && (
          <div className="relative rounded-xl overflow-hidden bg-black aspect-[4/3]" data-testid="scanner-video">
            <video ref={videoRef} className="w-full h-full object-cover" muted playsInline />
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute left-6 right-6 top-1/2 -translate-y-1/2 h-24 border-2 border-amber-400 rounded-lg" />
              <div className="absolute left-6 right-6 top-1/2 h-0.5 bg-amber-400 animate-pulse" />
            </div>
          </div>
        )}

        {(!supported || error) && (
          <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="scanner-fallback">
            {t("barcode_unsupported")}
          </div>
        )}

        <form onSubmit={submitManual} className="flex gap-2">
          <Input value={manual} onChange={(e) => setManual(e.target.value)} placeholder={t("enter_barcode")} className="h-12 rounded-lg font-mono" data-testid="scanner-manual-input" />
          <Button type="submit" className="h-12 bg-[#B45309] hover:bg-[#92400E] text-white rounded-xl" data-testid="scanner-manual-submit">OK</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
